import React, { useEffect, useState } from 'react';
import { useSelector } from 'react-redux';

import { selectSearchSignUpLink } from '../selectors/searchSelectors';

import '../../../../common/stylesheets/modules/search/subscribe-banner.less';

// RFPC-1342: dismissible "Choose human-made stock" subscribe banner shown to visitors on the
// video search page when the `messaging_updates` experiment is in the `test` variant. Matches
// Figma node 1171:23043. The banner reveals only after the user scrolls more than 20vh past the
// top of the page, and re-hides if they scroll back above that threshold (toggle behavior);
// dismissing it (X) hides it permanently via localStorage. Once revealed it floats at the bottom
// of the viewport; positioning, the responsive variants (images hidden below 1024px, banner
// hidden below 600px), and the per-breakpoint padding all live in subscribe-banner.less (viewport
// media queries — JIT is disabled so there are no arbitrary Tailwind classes here).

const DISMISSED_STORAGE_KEY = 'subscribeBannerDismissed';

// Reveal the banner once the user has scrolled more than 20% of the viewport height past the top.
const SCROLL_REVEAL_RATIO = 0.2;

const BANNER_IMAGES: string[] = [
  'search/subscribe-banner/human-made-1.png',
  'search/subscribe-banner/human-made-2.png',
  'search/subscribe-banner/human-made-3.png',
];

function isInitiallyDismissed(): boolean {
  return (
    typeof window !== 'undefined' &&
    !!window.localStorage &&
    localStorage.getItem(DISMISSED_STORAGE_KEY) === 'true'
  );
}

function SubscribeBanner() {
  const signUpLink = useSelector(selectSearchSignUpLink);
  const [dismissed, setDismissed] = useState<boolean>(isInitiallyDismissed);
  const [scrolledPastThreshold, setScrolledPastThreshold] =
    useState<boolean>(false);

  useEffect(() => {
    const onScroll = () => {
      setScrolledPastThreshold(
        window.scrollY > window.innerHeight * SCROLL_REVEAL_RATIO
      );
    };

    onScroll(); // sync initial state in case the page is restored mid-scroll
    window.addEventListener('scroll', onScroll, { passive: true });

    return () => window.removeEventListener('scroll', onScroll);
  }, []);

  const dismiss = () => {
    setDismissed(true);
    if (window.localStorage) {
      localStorage.setItem(DISMISSED_STORAGE_KEY, 'true');
    }
  };

  if (dismissed || !scrolledPastThreshold) {
    return null;
  }

  return (
    <div className="subscribe-banner">
      <div
        data-cy="subscribeBanner"
        className="subscribe-banner__inner flex items-center gap-6 bg-blue-100 py-2 pl-4 pr-8 text-gray-900"
      >
        <div
          className="subscribe-banner__images"
          data-cy="subscribeBannerImages"
        >
          {BANNER_IMAGES.map((src) => (
            <img
              key={src}
              src={`${__ASSETS_COMMON_IMAGES_URL__}/${src}`}
              alt=""
            />
          ))}
        </div>

        <div className="flex min-w-0 flex-1 flex-col gap-1">
          <span className="text-lg font-bold leading-6">
            Choose human-made stock
          </span>
          <span className="text-base">
            Get unlimited downloads that directly support artists
          </span>
        </div>

        <a
          href={signUpLink}
          className="flex h-10 shrink-0 items-center justify-center whitespace-nowrap rounded-lg bg-gray-800 px-6 text-sm font-semibold text-white hover:no-underline"
        >
          Subscribe to Download
        </a>

        <button
          type="button"
          aria-label="Dismiss"
          onClick={dismiss}
          className="flex shrink-0 cursor-pointer items-center justify-center border-0 bg-transparent p-0"
        >
          <img
            src={`${__ASSETS_COMMON_IMAGES_URL__}/icons/sb-close-black.svg`}
            alt=""
            width={16}
            height={16}
          />
        </button>
      </div>
    </div>
  );
}

export default SubscribeBanner;
